import type { AOEQuestionSchemaT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { Checkbox } from '@/shared/ui/shadcn/checkbox';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import type { ICD10CodeT } from '@/entities/labs';

import type { AoeAnswersById } from '../../model/aoeHelpers.js';
import { AoeForm } from '../aoe/AoeForm.js';
import { ICD10CodeSelector } from '../selectors/ICD10CodeSelector.js';

export interface DiseaseCodesStepProps {
  aoeSchema?: AOEQuestionSchemaT;
  isLoadingLabTest?: boolean;
  aoeAnswersById: Record<string, string>;
  onAoeChange: (answers: AoeAnswersById) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  notesMaxLength?: number;
  priority: boolean;
  onPriorityChange: (priority: boolean) => void;
  icdCodes: ICD10CodeT[];
  onAddIcdCode: (code: ICD10CodeT) => void;
  onRemoveIcdCode: (code: string) => void;
}

export function DiseaseCodesStep({
  aoeSchema,
  isLoadingLabTest = false,
  aoeAnswersById,
  onAoeChange,
  notes,
  onNotesChange,
  notesMaxLength,
  priority,
  onPriorityChange,
  icdCodes,
  onAddIcdCode,
  onRemoveIcdCode,
}: DiseaseCodesStepProps) {
  const hasAoe = (aoeSchema?.sections.length ?? 0) > 0;

  return (
    <div className="space-y-5">
      <ICD10CodeSelector selectedCodes={icdCodes} onAdd={onAddIcdCode} onRemove={onRemoveIcdCode} />

      {isLoadingLabTest && (
        <div className="space-y-3 py-2" role="status" aria-label="Loading test questions">
          <div className="h-3 w-2/5 animate-pulse rounded-full bg-gradient-to-r from-muted/50 via-muted/80 to-muted/50" />
          <div className="h-3 w-3/5 animate-pulse rounded-full bg-gradient-to-r from-muted/40 via-muted/70 to-muted/40 [animation-delay:150ms]" />
        </div>
      )}

      {aoeSchema && hasAoe && (
        <AoeForm schema={aoeSchema} answersById={aoeAnswersById} onChange={onAoeChange} />
      )}

      <Textarea
        placeholder="Clinical notes (optional)"
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        maxLength={notesMaxLength}
        rows={2}
      />
      {notesMaxLength !== undefined && (
        <p className="text-right text-xs text-muted-foreground">
          {notes.length}/{notesMaxLength}
        </p>
      )}

      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={priority} onCheckedChange={(c) => onPriorityChange(c === true)} />
        STAT / Priority order
      </label>
    </div>
  );
}
